// src/composables/useDashboardWS.js
import { ref, onBeforeUnmount } from 'vue'

// options.manual: when true, the socket is not opened on creation; callers
// drive it with the returned connect()/disconnect() (used by the chat bubble,
// which only connects for authenticated users with the chat permission).
export function useDashboardWS(stream, options) {
  const manual = !!(options && options.manual)
  const status = ref('closed') // 'closed' | 'open' | 'retry'
  let ws = null
  let retries = 0
  let stopped = false
  const listeners = []
  let pingTimer = null
  let retryTimer = null

  function notify(msg) {
    for (let i = 0; i < listeners.length; i += 1) {
      try {
        listeners[i](msg)
      } catch (e) {
        console.warn('WS listener error', e)
      }
    }
  }

  function schedulePing() {
    clearInterval(pingTimer)
    // lightweight keepalive for some proxies (optional)
    pingTimer = setInterval(() => {
      try {
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send('{"type":"ping"}')
        }
      } catch (e) {
        // ignore send errors; reconnect logic handles it
      }
    }, 25000)
  }

  function connect() {
    stopped = false
    clearTimeout(retryTimer)
    const isHttps = window.location.protocol === 'https:'
    const proto = isHttps ? 'wss' : 'ws'
    // Relative to FE host; dev proxy must forward /ws to Django
    const url = proto + '://' + window.location.host + '/ws/dashboard/' + stream + '/'
    ws = new WebSocket(url)

    ws.onopen = function () {
      status.value = 'open'
      retries = 0
      schedulePing()
    }

    ws.onmessage = function (evt) {
      try {
        const msg = JSON.parse(evt.data)
        notify(msg)
      } catch (e) {
        // Non-JSON messages are ignored (e.g., pongs)
        // console.warn('WS message parse error', e)
      }
    }

    ws.onclose = function () {
      clearInterval(pingTimer)
      if (stopped) {
        status.value = 'closed'
        return
      }
      status.value = 'retry'
      const backoff = Math.min(1000 * Math.pow(2, (retries += 1)), 15000)
      retryTimer = setTimeout(connect, backoff)
    }

    ws.onerror = function (err) {
      console.error('WS error', err)
      try {
        if (ws && ws.readyState !== WebSocket.CLOSED) {
          ws.close()
        }
      } catch (e) {
        console.warn('WS close error', e)
      }
    }
  }

  function onMessage(fn) {
    if (typeof fn === 'function') listeners.push(fn)
  }

  function disconnect() {
    stopped = true
    clearTimeout(retryTimer)
    clearInterval(pingTimer)
    status.value = 'closed'
    try {
      if (ws) ws.close()
    } catch (e) {
      console.warn('WS close error', e)
    }
  }

  if (!manual) connect()

  onBeforeUnmount(function () {
    try {
      disconnect()
    } catch (e) {
      console.warn('WS unmount close error', e)
    }
  })

  return { status, onMessage, connect, disconnect }
}
